import { useEffect, useId, useRef, type KeyboardEvent, type ReactNode } from 'react';

import styles from './Dialog.module.css';

export interface DialogProps {
  title: string;
  /** Above the title, e.g. what kind of thing the dialog is about. */
  eyebrow?: ReactNode;
  description?: ReactNode;
  /** Called on Escape, the close button and a backdrop click, unless `dismissable` is false. */
  onClose: () => void;
  /** False while a write is in flight, so it cannot be abandoned half-way. */
  dismissable?: boolean;
  children?: ReactNode;
  /** The footer buttons, right-aligned. */
  actions?: ReactNode;
  /** `drawer` slides in from the right and fills the window's height. */
  placement?: 'center' | 'drawer';
}

const FOCUSABLE =
  'a[href], button:not(:disabled), input:not(:disabled), select:not(:disabled), textarea:not(:disabled), [tabindex]:not([tabindex="-1"])';

/** A modal: focus moves in on open, stays inside, and returns where it was on close. */
export function Dialog({
  title,
  eyebrow,
  description,
  onClose,
  dismissable = true,
  children,
  actions,
  placement = 'center',
}: DialogProps) {
  const panel = useRef<HTMLDivElement>(null);
  const titleId = useId();
  const descriptionId = useId();

  useEffect(() => {
    const previous = document.activeElement as HTMLElement | null;
    const first =
      panel.current?.querySelector<HTMLElement>('[data-autofocus]') ??
      Array.from(panel.current?.querySelectorAll<HTMLElement>(FOCUSABLE) ?? []).find(
        (element) => !element.dataset.close,
      );
    (first ?? panel.current)?.focus();
    return () => previous?.focus();
  }, []);

  function onKeyDown(event: KeyboardEvent<HTMLDivElement>) {
    if (event.key === 'Escape') {
      event.stopPropagation();
      if (dismissable) {
        onClose();
      }
      return;
    }
    if (event.key !== 'Tab' || !panel.current) {
      return;
    }
    const items = Array.from(panel.current.querySelectorAll<HTMLElement>(FOCUSABLE));
    if (items.length === 0) {
      return;
    }
    const [first, last] = [items[0], items[items.length - 1]];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  return (
    <div
      className={`${styles.backdrop} ${styles[placement]}`}
      onMouseDown={(event) => {
        if (event.target === event.currentTarget && dismissable) {
          onClose();
        }
      }}
    >
      <div
        ref={panel}
        aria-describedby={description ? descriptionId : undefined}
        aria-labelledby={titleId}
        aria-modal="true"
        className={styles.panel}
        onKeyDown={onKeyDown}
        role="dialog"
        tabIndex={-1}
      >
        <div className={styles.header}>
          <div className={styles.heading}>
            {eyebrow ? <div className={styles.eyebrow}>{eyebrow}</div> : null}
            <h2 className={styles.title} id={titleId}>
              {title}
            </h2>
            {description ? (
              <div className={styles.description} id={descriptionId}>
                {description}
              </div>
            ) : null}
          </div>
          <button
            aria-label="Close"
            className={styles.close}
            data-close="true"
            disabled={!dismissable}
            onClick={onClose}
            type="button"
          >
            <svg aria-hidden="true" viewBox="0 0 16 16" width="14" height="14">
              <path
                d="M3.5 3.5l9 9M12.5 3.5l-9 9"
                fill="none"
                stroke="currentColor"
                strokeLinecap="round"
                strokeWidth="1.6"
              />
            </svg>
          </button>
        </div>
        {children ? <div className={styles.body}>{children}</div> : null}
        {actions ? <div className={styles.actions}>{actions}</div> : null}
      </div>
    </div>
  );
}
